/**
 * ImpersonationContext — admin-side "View as another user" state.
 *
 * Persists the target to localStorage so Axios' request interceptor
 * (lib/api.ts) reads the same key and attaches the
 * `X-Impersonate-User` header on every backend call.
 *
 * AuthContext is intentionally unaffected — `useAuth().user` keeps
 * returning the REAL admin (Siqi). Role-aware UI should use
 * `useEffectiveUser()` which collapses (real, target) to the
 * effective identity for the current view.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { IMPERSONATE_STORAGE_KEY, TOKEN_STORAGE_KEY } from '../lib/constants'

export interface ImpersonationTarget {
  id: number
  name: string
  email: string
  role: 'member' | 'talent' | 'council' | 'admin' | 'super_admin'
  focus_area_code?: string | null
  preferred_focus_areas?: string[]
  is_test_account?: boolean
  // Profile fields (so Profile page can show the impersonated user's
  // bio / github / visibility, not the real admin's)
  bio?: string | null
  github_handle?: string | null
  visible_in_directory?: boolean
  talent_profile_active?: boolean
  dsai_cluster_lead?: string | null
  // MS Graph enrichment
  job_title?: string | null
  department?: string | null
  office_location?: string | null
  company?: string | null
  country?: string | null
}

interface ImpersonationContextValue {
  target: ImpersonationTarget | null
  setTarget(next: ImpersonationTarget): void
  /** Merge fresh fields into the current target, e.g. after a profile write. */
  updateTarget(patch: Partial<ImpersonationTarget>): void
  clearTarget(): void
}

const ImpersonationContext = createContext<ImpersonationContextValue | null>(null)

function readStorage(): ImpersonationTarget | null {
  try {
    const raw = localStorage.getItem(IMPERSONATE_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as ImpersonationTarget) : null
  } catch {
    return null
  }
}

export function useImpersonation(): ImpersonationContextValue {
  const ctx = useContext(ImpersonationContext)
  if (!ctx) throw new Error('useImpersonation must be used within ImpersonationProvider')
  return ctx
}

/** Like useImpersonation, but null outside a provider instead of throwing —
 *  for hooks that also run where nobody can be impersonating. */
export function useImpersonationOptional(): ImpersonationContextValue | null {
  return useContext(ImpersonationContext)
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [target, setTargetState] = useState<ImpersonationTarget | null>(() => readStorage())

  useEffect(() => {
    if (target) {
      localStorage.setItem(IMPERSONATE_STORAGE_KEY, JSON.stringify(target))
    } else {
      localStorage.removeItem(IMPERSONATE_STORAGE_KEY)
    }
  }, [target])

  // Validate the stored target ONCE on mount by hitting /auth/me with
  // the impersonation header attached. We bypass the api.ts request
  // interceptor (which skips impersonation for /auth/me) by calling
  // fetch directly. If the backend returns 404 with the impersonation-
  // target-not-found detail, clear the stored target so no other call
  // ever sees the bad header.
  useEffect(() => {
    const stored = readStorage()
    if (!stored) return
    const ctrl = new AbortController()
    const token = localStorage.getItem(TOKEN_STORAGE_KEY)
    fetch('/api/auth/me', {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-Impersonate-User': String(stored.id),
      },
      signal: ctrl.signal,
    })
      .then(async (res) => {
        // The same call returns the target's full profile. The picker's list
        // (GET /admin/users) is a subset with no preferred_focus_areas, so
        // without this the profile page showed View-as talents with none.
        if (res.ok) {
          const fresh = (await res.json().catch(() => null)) as Partial<ImpersonationTarget> | null
          if (fresh?.id === stored.id) {
            setTargetState((prev) => (prev && prev.id === fresh.id ? { ...prev, ...fresh } : prev))
          }
          return
        }
        if (res.status !== 404) return
        const body = await res.json().catch(() => ({} as { detail?: string }))
        if (
          typeof body.detail === 'string' &&
          body.detail.startsWith('Impersonation target user ')
        ) {
          localStorage.removeItem(IMPERSONATE_STORAGE_KEY)
          setTargetState(null)
        }
      })
      .catch(() => { /* network errors surface elsewhere */ })
    return () => ctrl.abort()
  }, [])

  function setTarget(next: ImpersonationTarget) {
    // Write to localStorage synchronously BEFORE navigation — relying on
    // the useEffect to persist + then reload is a race the reload wins,
    // so the new value never makes it to disk and the impersonation
    // looks like it never happened.
    try {
      localStorage.setItem(IMPERSONATE_STORAGE_KEY, JSON.stringify(next))
    } catch { /* localStorage unavailable */ }
    setTargetState(next)
    // Navigate to home so HomeRouter routes to the new user's context
    window.location.href = '/'
  }

  function updateTarget(patch: Partial<ImpersonationTarget>) {
    setTargetState((prev) => (prev ? { ...prev, ...patch, id: prev.id } : prev))
  }

  function clearTarget() {
    try {
      localStorage.removeItem(IMPERSONATE_STORAGE_KEY)
    } catch { /* ignore */ }
    setTargetState(null)
    window.location.href = '/'
  }

  return (
    <ImpersonationContext.Provider value={{ target, setTarget, updateTarget, clearTarget }}>
      {children}
    </ImpersonationContext.Provider>
  )
}
